/**
 * 平台简介页面组件
 * 数据从API获取，移除硬编码统计数据
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Card, Col, Row, Spin } from 'antd';
import ContentRenderer from '../../../../components/common/ContentRenderer';
import { PlatformInfo, platformService } from '../../../../services/platformService';
import { useDict } from '../../../../hooks/useDict';
import './PlatformPage.css';

interface PlatformPageProps {
    platformInfo?: PlatformInfo;
    loading: boolean;
}

const PlatformPage: React.FC<PlatformPageProps> = ({ platformInfo, loading }) => {
    // 从字典获取统计数据标签（替代硬编码）
    const { dictData: statisticsItemDict } = useDict('statistics_item');
    
    // 统计数据配置（用于过滤显示的统计项）
    const [statisticsConfigs, setStatisticsConfigs] = useState<any[]>([]);

    // 加载统计数据配置
    useEffect(() => {
        const loadConfigs = async () => {
            try {
                const response = await platformService.getStatisticsConfig();
                if (response.code === 200 && response.data) {
                    setStatisticsConfigs(response.data || []);
                }
            } catch (error) {
                console.error('加载统计数据配置失败:', error);
            }
        };
        loadConfigs();
    }, []);

    // 统计数据映射（从字典动态获取，替代硬编码）
    const statisticsLabels = useMemo(() => {
        if (!statisticsItemDict || statisticsItemDict.length === 0) {
            return {};
        }
        // 从字典数据构建标签映射
        const labels: Record<string, string> = {};
        statisticsItemDict.forEach(item => {
            if (item.dictValue && item.dictLabel) {
                labels[item.dictValue] = item.dictLabel;
            }
        });
        return labels;
    }, [statisticsItemDict]);
    
    // 根据配置表过滤统计数据，只显示配置表中存在的统计项
    const filteredStatisticsMap = useMemo(() => {
        if (!platformInfo?.statisticsMap || statisticsConfigs.length === 0) {
            return platformInfo?.statisticsMap || {};
        }
        
        // 构建配置键集合
        const configKeysSet = new Set<string>();
        statisticsConfigs.forEach((config: any) => {
            const combinedKey = `${config.category}_${config.displayName}`;
            configKeysSet.add(combinedKey);
        });
        
        // 过滤统计数据，只保留配置表中存在的配置项
        const filtered: Record<string, any> = {};
        Object.entries(platformInfo.statisticsMap).forEach(([key, value]) => {
            if (configKeysSet.has(key)) {
                filtered[key] = value;
            }
        });
        
        return filtered;
    }, [platformInfo?.statisticsMap, statisticsConfigs]);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <p style={{ marginTop: '16px' }}>加载中...</p>
            </div>
        );
    }

    return (
        <div className="platform-page">
            <div className="platform-banner">
                <div className="banner-content">
                    <div className="banner-text">
                        <h1 className="banner-title">平台简介</h1>
                        <p className="banner-subtitle">了解知识产权服务业集聚区平台功能和服务</p>
                    </div>
                </div>
            </div>

            <div className="platform-content">
                <Card className="intro-card">
                    <div className="content-section">
                        {platformInfo ? (
                            <>
                                <ContentRenderer content={platformInfo.content || ''} />
                                {platformInfo.achievements && (
                                    <div className="achievements-content">
                                        <ContentRenderer content={platformInfo.achievements} />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="default-content">
                                <p>黄石经济技术开发区·铁山区知识产权服务业集聚区是于2017年6月7日由国家知识产权局批复设立的立足于智能制造产业集聚区的开展知识产权保护工作的服务性机构。</p>
                                <p>集聚区位于黄石市经济技术开发区，致力于为黄石市及周边地区提供专业的知识产权服务。</p>
                            </div>
                        )}
                    </div>

                    {/* 统计数据展示（从API获取，根据配置表过滤） */}
                    {filteredStatisticsMap && Object.keys(filteredStatisticsMap).length > 0 && (
                        <div className="achievement-stats">
                            <Row gutter={[16, 16]}>
                                {Object.entries(filteredStatisticsMap).map(([key, value]) => {
                                    // 解析组合键：category_displayName 或直接使用key
                                    const keyParts = key.split('_');
                                    const displayName = keyParts.length >= 2 ? keyParts.slice(1).join('_') : key;
                                    
                                    // 先从字典查找标签
                                    let label = statisticsLabels[displayName];
                                    
                                    // 如果字典找不到，尝试从配置中查找
                                    if (!label) {
                                        const config = statisticsConfigs.find((config: any) => {
                                            const combinedKey = `${config.category}_${config.displayName}`;
                                            return combinedKey === key;
                                        });
                                        // 如果配置中有 label 字段，使用它；否则使用 displayName
                                        label = config?.label || config?.displayName || displayName;
                                    }
                                    
                                    // 如果还是找不到，使用 displayName
                                    if (!label) {
                                        label = displayName;
                                    }
                                    
                                    return (
                                        <Col xs={12} sm={8} md={6} lg={4} key={key}>
                                            <div className="stat-item">
                                                <div className="stat-number">{value || 0}</div>
                                                <div className="stat-label">{label}</div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default PlatformPage;

